import { useEffect, useRef } from 'react';
import { useAuth } from './useAuth';
import { useStomp } from '../providers/StompProvider/hooks';

interface GameSubscribeOptions {
  // Runs after the subscribe emit succeeds.
  onSent?: () => void;
  // Return true if the first snapshot (e.g. balance) still hasn't arrived.
  needsResync?: () => boolean;
}

// Ask the backend to start streaming this game once connected and the user
// is known. Retries the emit, and asks again once if the first snapshot missed.
export function useGameSubscribeEmit(
  gameId: string,
  options?: GameSubscribeOptions
): void {
  const { client, connected, emitMessage } = useStomp();
  const { user } = useAuth();

  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    // No gameId means the caller has nothing to subscribe to — a game
    // edition whose backend code has not been issued yet. Publishing
    // `{ gameId: '' }` would be a malformed frame that still reports
    // itself as sent.
    if (!client || !connected || !user || !gameId) return;

    const retries: ReturnType<typeof setTimeout>[] = [];
    const doSubscribe = () => {
      const sent = emitMessage('/app/game/subscribe', { gameId });
      if (sent) {
        optionsRef.current?.onSent?.();
      } else {
        retries.push(setTimeout(doSubscribe, 500));
      }
    };

    const initial = setTimeout(doSubscribe, 100);
    // Safety net: if the first snapshot is still missing after 1s, ask again.
    const resync = setTimeout(() => {
      if (optionsRef.current?.needsResync?.()) {
        console.info('[subscribe] snapshot missing after 1s — re-requesting');
        doSubscribe();
      }
    }, 1000);

    return () => {
      clearTimeout(initial);
      clearTimeout(resync);
      retries.forEach(clearTimeout);
    };
  }, [client, connected, emitMessage, user, gameId]);
}
